import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import FadeIn from 'react-fade-in';

import IconHeart2 from 'components/icons/iconHeart2';
import VideoPlayIconWrapper from 'components/consumer/VideoPlayIconWrapper/VideoPlayIconWrapper';
import MobileModal from 'components/consumer/MobileModal';
import JBImage from 'components/essentials/JBImage';
import isClient from 'commons/isClient';
import IconLightningBoltFilled from 'components/icons/IconLightningBoltFilled';
import { colorYellow } from 'styles/layout/colors';
import withBrowserOnly from 'components/hoc/withBrowserOnly';
import useScreen from 'hooks/useScreen';
import ProtectionPlanVariant from './ProtectionPlanVariant';

const FlyoutContainer = ({
  align = 'left',
  className = '',
  content,
  fallbackSrc,
  flyoutVariant = 'standard',
  headerBgColor = null,
  imageUrl,
  isInFlashSale = false,
  isOpen,
  left,
  mobileContentNoPadding = false,
  onClose,
  protectionPlan = null,
  showAboveOnDesktop = false,
  showFavIcon,
  subSubTitle,
  subTitle,
  title,
  titleExtra,
  titleInfo,
  top,
  useCoordsFromProps,
  videoObj,
}) => {
  const containerRef = useRef(null);
  const [height, setHeight] = useState(300);
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);

  const { isMobile, isDesktop } = useScreen();

  const [isPlaying, setPlaying] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current && isPlaying) {
      videoRef.current.play();
    }
  }, [isPlaying]);

  useEffect(() => {
    // wait for fade-in to get container height
    setTimeout(() => {
      const theHeight = containerRef.current?.getBoundingClientRect().height;

      if (theHeight) {
        setHeight(theHeight);
      }

      setIsAnimationComplete(true);
    }, 100);
  }, []);

  let absTop = flyoutVariant === 'materialSwatch' ? -30 : 0;

  // if container is at the top of the screen
  if (top < 0) {
    absTop = -top;
  }
  // if container is at the bottom of the screen
  else if (isClient() && window.innerHeight < top + height) {
    absTop = window.innerHeight - height - top;
    if (flyoutVariant === 'fabricGallery') {
      absTop -= 150;
    }
  }

  const videoContent =
    !imageUrl && videoObj ? (
      <div
        className={classNames(
          'min-h-[156px] overflow-hidden object-cover w-screen relative lg:w-auto',
          '[&_video]:w-full'
        )}
      >
        <video
          className="FlyoutContainer__video"
          poster={videoObj.src}
          ref={videoRef}
          controls={videoObj.controls === false ? videoObj.controls : isPlaying}
          autoPlay={videoObj.autoPlay}
          muted={videoObj.autoPlay}
          loop={videoObj.loop}
          onClick={() => setPlaying(!isPlaying)}
          onPause={() => setPlaying(false)}
          key={videoObj.link}
          playsInline="playsinline"
        >
          <track kind="captions" />
          <source src={videoObj.link} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        {!videoObj.autoPlay && !isPlaying[0] && (
          <VideoPlayIconWrapper
            onClick={() => setPlaying([true, isPlaying[1]])}
          />
        )}
      </div>
    ) : null;

  const containerContent = (
    <>
      {imageUrl && !isMobile && (
        <div
          className={classNames(
            'relative lg:w-auto [&_img]:h-[156px]',
            '[&_svg]:absolute [&_svg]:bottom-4 [&_svg]:right-4',
            {
              'w-screen [&_img]:object-cover': headerBgColor !== 'grey',
              'w-auto [&_img]:object-contain px-0 py-2 bg-gray-light7':
                headerBgColor === 'grey',
            }
          )}
        >
          <JBImage
            src={imageUrl}
            fallbackSrc={fallbackSrc}
            width={400}
            loader={false}
            lazy={false}
          />
          {showFavIcon ? <IconHeart2 color="white" fill="white" /> : null}
        </div>
      )}
      {!imageUrl && videoObj && !isMobile && videoContent}
      <div className="text-xs pt-6 px-2 pb-0 lg:border lg:border-solid lg:border-gray-light7 lg:rounded-bl-lg lg:rounded-br-lg lg:p-8">
        {!!isInFlashSale && (
          <div className="items-center flex mb-2">
            <IconLightningBoltFilled
              className="mr-[5px]"
              color={colorYellow}
              height={13}
              width={12}
            />
            <span className="text-xs font-bold text-gray text-left">
              Hurry - save big on this flash sale!
            </span>
          </div>
        )}

        <div className="flex">
          <span className="text-xl font-bold">
            {title}
            {titleExtra ? (
              <span className="text-lg font-bold ml-1">{titleExtra}</span>
            ) : null}
          </span>
          {titleInfo ? (
            <span className="text-base font-bold ml-1.5">({titleInfo})</span>
          ) : null}
        </div>
        {!!subTitle || !!subSubTitle ? (
          <div className="mb-4">
            {subTitle ? <span> {subTitle} </span> : null}
            {subSubTitle ? (
              <div className="FlyoutContainer__subSubTitle">
                {' '}
                {subSubTitle}{' '}
              </div>
            ) : null}
          </div>
        ) : null}
        <div className="text-xs text-gray text-left">{content}</div>
      </div>
    </>
  );

  if (isMobile) {
    const finalContentUnderHeader = imageUrl ? (
      <div
        className={classNames(
          'relative lg:w-auto [&_img]:h-[156px]',
          '[&_svg]:absolute [&_svg]:bottom-4 [&_svg]:right-4',
          {
            'w-screen [&_img]:object-cover': headerBgColor !== 'grey',
            'w-auto [&_img]:object-contain px-0 py-2 bg-gray-light7':
              headerBgColor === 'grey',
          }
        )}
      >
        <JBImage
          src={imageUrl}
          fallbackSrc={fallbackSrc}
          width={400}
          loader={false}
          lazy={false}
        />
        {showFavIcon ? <IconHeart2 color="white" fill="white" /> : null}
      </div>
    ) : !imageUrl && videoObj ? (
      videoContent
    ) : null;
    return (
      <MobileModal
        headerCenter=""
        isFullHeight={false}
        isOpen={isOpen}
        onClose={onClose}
        contentNoPadding={mobileContentNoPadding}
        contentUnderHeader={finalContentUnderHeader}
        className={className}
      >
        {containerContent}
      </MobileModal>
    );
  }

  const showAbove = isDesktop && showAboveOnDesktop;
  const horizontalOffset = showAbove && isAnimationComplete ? -290 : undefined;

  return (
    <div className="relative z-[1]">
      <FadeIn>
        <div
          ref={containerRef}
          className={classNames(
            'w-[400px] min-h-[255px] bg-white ml-[1.9rem] rounded-lg overflow-hidden lg:absolute lg:z-10 lg:top-0',
            className,
            {
              'lg:left-[-450px]': align !== 'right',
              'left-auto right-[-430px]': align === 'right',
            }
          )}
          style={{
            visibility:
              showAbove && !isAnimationComplete ? 'hidden' : undefined,
            top: useCoordsFromProps
              ? top
              : showAbove && isAnimationComplete
              ? 0 - (height + 38)
              : absTop,
            left: useCoordsFromProps
              ? left
              : align === 'left'
              ? horizontalOffset
              : undefined,
            right: align === 'right' ? horizontalOffset : undefined,
          }}
        >
          {flyoutVariant === 'protectionPlan' && protectionPlan ? (
            <ProtectionPlanVariant {...protectionPlan} />
          ) : (
            containerContent
          )}
        </div>
      </FadeIn>
    </div>
  );
};

FlyoutContainer.propTypes = {
  align: PropTypes.oneOf(['left', 'right']),
  className: PropTypes.string,
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  fallbackSrc: PropTypes.string,
  flyoutVariant: PropTypes.oneOf([
    'materialSwatch',
    'protectionPlan',
    'standard',
  ]),
  headerBgColor: PropTypes.oneOf(['grey']),
  imageUrl: PropTypes.string,
  isInFlashSale: PropTypes.bool,
  isOpen: PropTypes.bool,
  left: PropTypes.bool,
  mobileContentNoPadding: PropTypes.bool,
  onClose: PropTypes.func,
  protectionPlan: PropTypes.shape({
    backgroundImg: PropTypes.string,
    planItems: PropTypes.arrayOf(PropTypes.string),
    planName: PropTypes.string,
    planType: PropTypes.oneOf(['protection', 'pet']),
  }),
  showAboveOnDesktop: PropTypes.bool,
  showFavIcon: PropTypes.bool,
  subSubTitle: PropTypes.string,
  subTitle: PropTypes.node,
  title: PropTypes.string,
  titleExtra: PropTypes.string,
  titleInfo: PropTypes.string,
  top: PropTypes.number,
  useCoordsFromProps: PropTypes.bool,
  videoObj: PropTypes.shape({
    autoPlay: PropTypes.bool,
    controls: PropTypes.object,
    link: PropTypes.string,
    loop: PropTypes.bool,
    src: PropTypes.string,
  }),
};

export default withBrowserOnly(FlyoutContainer);
